import styled from '@emotion/styled'
import {
    StyledCommonwealthGamesWidgetCtaArrow,
    StyledCommonwealthGamesWidgetCtaButton,
    StyledCommonwealthGamesWidgetCtaLink,
} from '../CommonwealthGamesWidgetCta/CommonwealthGamesWidgetCta.styled'
import {
    breakpoint,
    calcRem,
    colors,
    fonts,
    themedValue,
} from '../../../__styling'
import type {
    CommonwealthGamesMedalTallyWidgetColorMode,
    CommonwealthGamesMedalTallyWidgetSize,
} from './CommonwealthGamesMedalTallyWidget'

export interface StyledMedalTallyTableWrapperProps {
    size?: CommonwealthGamesMedalTallyWidgetSize
    colorMode?: CommonwealthGamesMedalTallyWidgetColorMode
    scrollMaxHeight?: number
}

export const StyledMedalTallyTableWrapper = styled('div', {
    shouldForwardProp: (prop) =>
        prop !== 'size' && prop !== 'colorMode' && prop !== 'scrollMaxHeight',
})<StyledMedalTallyTableWrapperProps>(
    ({ theme, size = 'default', colorMode = 'light', scrollMaxHeight }) => ({
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowX: 'auto',
        overflowY: typeof scrollMaxHeight === 'number' ? 'auto' : 'visible',
        width: '100%',
        maxHeight:
            typeof scrollMaxHeight === 'number'
                ? calcRem(scrollMaxHeight)
                : undefined,
        padding: calcRem(0, 10, 12),
        borderRadius: calcRem(16),
        '--cg-widget-bg':
            colorMode === 'dark'
                ? colors.sevennews.codGray
                : themedValue(theme, {
                      sevennews: colors.sevennews.white,
                      fallback: colors.white,
                  }),
        '--cg-widget-text':
            colorMode === 'dark'
                ? colors.white
                : themedValue(theme, {
                      sevennews: colors.commonwealthGamesBlue,
                      fallback: colors.black,
                  }),
        '--cg-widget-muted-text':
            colorMode === 'dark'
                ? colors.white80
                : themedValue(theme, {
                      sevennews: colors.sevennews.stormGrey,
                      fallback: colors.black50,
                  }),
        border: `1px solid ${themedValue(theme, {
            sevennews: colors.sevennews.alto,
            fallback: colors.black20,
        })}`,
        boxShadow: `0 ${calcRem(2)} ${calcRem(18)} ${colors.black005}`,
        backgroundColor: 'var(--cg-widget-bg)',
        marginBottom: size === 'small' ? calcRem(16) : 0,
        [breakpoint('sm')]: {
            marginBottom: 0,
        },
        ...(size === 'default' ? getDefaultDesktopStyles() : {}),
    }),
)

export const StyledMedalTallyTable = styled('table')(
    {
        flexGrow: 1,
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: calcRem(10),
    },
    ({ theme }) => ({
        fontFamily: themedValue(theme, {
            sevennews: fonts.sevennews.sansSerif,
            thenightly: fonts.thenightly.sansSerif,
            perthnow: fonts.perthnow.sansSerif,
            fallback: fonts.thewest.sansSerif,
        }),
        color: 'var(--cg-widget-text)',
    }),
)

export const StyledMedalTallyHeadCell = styled('th')(
    {
        padding: calcRem(8, 3),
        fontSize: calcRem(12),
        lineHeight: calcRem(16),
        fontWeight: 600,
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    ({ theme }) => ({
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'var(--cg-widget-bg)',
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            backgroundColor: themedValue(theme, {
                sevennews: colors.sevennews.alto,
                fallback: colors.black20,
            }),
        },
    }),
)

export const StyledMedalTallyCell = styled('td')(
    {
        padding: calcRem(8, 3),
        fontSize: calcRem(14),
        lineHeight: calcRem(18),
        verticalAlign: 'middle',
    },
    ({ theme }) => ({
        borderBottom: `1px solid ${themedValue(theme, {
            sevennews: colors.sevennews.alto,
            fallback: colors.black20,
        })}`,
        'tbody > tr:first-child &': {
            borderTop: 0,
        },
    }),
)

export const StyledRankCell = styled(StyledMedalTallyCell)({
    width: calcRem(28),
    textAlign: 'left',
})

export const StyledCountryCell = styled(StyledMedalTallyCell)({
    minWidth: calcRem(110),
})

export const StyledNumberCell = styled(StyledMedalTallyCell)({
    width: calcRem(40),
    textAlign: 'left',
})

export const StyledTotalHeadCell = styled(StyledMedalTallyHeadCell)({
    textAlign: 'center',
})

export const StyledTotalCell = styled(StyledNumberCell)({
    textAlign: 'center',
})

export const StyledMedalHeader = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: calcRem(2),
    justifyContent: 'center',
    width: '100%',
})

export const StyledMedalHeaderLabel = styled('span')({
    display: 'none',
})

export const StyledSortButton = styled('button')<{
    isActive?: boolean
    align?: 'left' | 'center'
}>(({ theme, isActive = false, align = 'center' }) => ({
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    margin: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: align === 'left' ? 'flex-start' : 'center',
    width: '100%',
    color: 'var(--cg-widget-text)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: isActive ? 700 : 600,
    textAlign: 'inherit',
    textDecoration: isActive ? 'underline' : 'none',
    textUnderlineOffset: calcRem(2),
    ':disabled': {
        cursor: 'default',
    },
}))

export const StyledMedalValueCircle = styled('span')<{
    medalType: 'gold' | 'silver' | 'bronze'
}>(({ medalType }) => {
    const backgroundColorByMedalType = {
        gold: '#F1AF0F',
        silver: '#CDCCCE',
        bronze: '#E4780A',
    }

    const colorByMedalType = {
        gold: colors.commonwealthGamesBlue,
        silver: colors.commonwealthGamesBlue,
        bronze: colors.sevennews.white,
    }

    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: calcRem(20),
        width: calcRem(20),
        borderRadius: '50%',
        backgroundColor: backgroundColorByMedalType[medalType],
        color: colorByMedalType[medalType],
        fontSize: calcRem(11),
        lineHeight: calcRem(14),
        fontWeight: 600,
        flexShrink: 0,
    }
})

export const StyledMedalValuePill = styled('span')<{
    medalType: 'gold' | 'silver' | 'bronze'
}>(({ medalType }) => {
    const backgroundColorByMedalType = {
        gold: '#FBEEC5',
        silver: '#EEEEEE',
        bronze: '#FCE9D4',
    }

    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: calcRem(34),
        padding: calcRem(3, 7),
        borderRadius: calcRem(7),
        backgroundColor: backgroundColorByMedalType[medalType],
        color: colors.commonwealthGamesBlue,
        fontSize: calcRem(12),
        lineHeight: calcRem(14),
        fontWeight: 600,
    }
})

export const StyledTotalValue = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: calcRem(24),
    fontSize: calcRem(13),
    lineHeight: calcRem(16),
    fontWeight: 700,
})

export const StyledCountry = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: calcRem(5),
})

export const StyledCountryFlag = styled('img')({
    width: calcRem(24),
    height: calcRem(20),
    objectFit: 'contain',
    borderRadius: calcRem(3),
    flexShrink: 0,
})

export const StyledCountryName = styled('span')({
    display: 'inline-block',
    fontSize: calcRem(14),
    lineHeight: calcRem(18),
})

export const StyledFullMedalTallyButtonWrapper = styled('div')({
    marginTop: 'auto',
    paddingTop: calcRem(8),
    '& .js-skeleton [class*="StyledSkeleton"]': {
        borderRadius: calcRem(8),
    },
})

export const StyledWidgetState = styled('div', {
    shouldForwardProp: (prop) => prop !== 'minHeight',
})<{ minHeight?: number }>(({ theme, minHeight }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: calcRem(12),
    minHeight:
        typeof minHeight === 'number' ? calcRem(minHeight) : calcRem(320),
    padding: calcRem(20, 16),
    textAlign: 'center',
    [breakpoint('sm')]: {
        padding: calcRem(24),
    },
}))

export const StyledWidgetPlaceholder = styled('div', {
    shouldForwardProp: (prop) => prop !== 'placeholderHeight',
})<{
    placeholderHeight?: number
}>(({ placeholderHeight }) => ({
    minHeight:
        typeof placeholderHeight === 'number'
            ? calcRem(placeholderHeight)
            : calcRem(320),
    display: 'flex',
    alignItems: 'stretch',
    marginTop: calcRem(10),
    '& .js-skeleton': {
        width: '100%',
    },
    '& .js-skeleton [class*="StyledSkeleton"]': {
        borderRadius: calcRem(12),
        margin: 0,
        height: '100%',
    },
    [breakpoint('sm')]: {
        marginTop: calcRem(18),
    },
}))

export const StyledWidgetErrorTitle = styled('h3')(({ theme }) => ({
    margin: 0,
    fontFamily: themedValue(theme, {
        sevennews: fonts.sevennews.sansSerif,
        thenightly: fonts.thenightly.sansSerif,
        perthnow: fonts.perthnow.sansSerif,
        fallback: fonts.thewest.sansSerif,
    }),
    color: 'var(--cg-widget-text)',
    fontSize: calcRem(18),
    lineHeight: calcRem(22),
    fontWeight: 600,
}))

export const StyledWidgetErrorText = styled('p')(({ theme }) => ({
    margin: 0,
    fontFamily: themedValue(theme, {
        sevennews: fonts.sevennews.sansSerif,
        thenightly: fonts.thenightly.sansSerif,
        perthnow: fonts.perthnow.sansSerif,
        fallback: fonts.thewest.sansSerif,
    }),
    color: 'var(--cg-widget-text)',
    fontSize: calcRem(14),
    lineHeight: calcRem(20),
}))

function getDefaultDesktopStyles(): Record<string, any> {
    return {
        [breakpoint('sm')]: {
            padding: calcRem(0, 18, 18),
            [`& ${StyledMedalTallyHeadCell}`]: {
                padding: calcRem(12, 10),
                fontSize: calcRem(14),
                lineHeight: calcRem(18),
            },
            [`& ${StyledMedalTallyCell}`]: {
                padding: calcRem(12, 10),
                fontSize: calcRem(16),
                lineHeight: calcRem(20),
            },
            [`& ${StyledRankCell}`]: {
                width: calcRem(56),
            },
            [`& ${StyledCountryCell}`]: {
                minWidth: calcRem(220),
            },
            [`& ${StyledNumberCell}`]: {
                width: calcRem(72),
            },
            [`& ${StyledMedalHeader}`]: {
                justifyContent: 'flex-start',
                width: 'auto',
                gap: calcRem(8),
            },
            [`& ${StyledMedalHeaderLabel}`]: {
                display: 'inline',
            },
            [`& ${StyledMedalValueCircle}`]: {
                height: calcRem(26),
                width: calcRem(26),
                fontSize: calcRem(16),
                lineHeight: calcRem(20),
            },
            [`& ${StyledMedalValuePill}`]: {
                minWidth: calcRem(48),
                padding: calcRem(6, 12),
                fontSize: calcRem(16),
                lineHeight: calcRem(20),
            },
            [`& ${StyledTotalValue}`]: {
                minHeight: calcRem(32),
                fontSize: calcRem(16),
                lineHeight: calcRem(20),
            },
            [`& ${StyledCountry}`]: {
                gap: calcRem(12),
            },
            [`& ${StyledCountryFlag}`]: {
                width: calcRem(34),
                height: calcRem(28),
            },
            [`& ${StyledCountryName}`]: {
                fontSize: calcRem(16),
                lineHeight: calcRem(20),
                whiteSpace: 'normal',
            },
            [`& ${StyledCommonwealthGamesWidgetCtaButton}, & ${StyledCommonwealthGamesWidgetCtaLink}`]:
                {
                    fontSize: calcRem(16),
                    lineHeight: calcRem(20),
                    minHeight: calcRem(46),
                    padding: calcRem(12, 56, 12, 24),
                },
            [`& ${StyledCommonwealthGamesWidgetCtaArrow}`]: {
                right: calcRem(18),
            },
            [`& ${StyledMedalTallyTable}`]: {
                marginTop: calcRem(18),
            },
        },
    }
}
